import { subscriptionAtom } from '@/atoms/subscription.atom';
import { useOrganization, useOrganizations } from '@/hooks/useOrganizations';
import { useAtomValue } from 'jotai';
import { AlertTriangle, CreditCard, XCircle } from 'lucide-react';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';

const SubscriptionExpiredOverlay: FunctionComponent = () => {
  const subscription = useAtomValue(subscriptionAtom);
  const organization = useOrganization();
  const { hasRole } = useOrganizations();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('subscription');

  const billingStatus = subscription.billingStatus;

  // Only show for non-active subscription states that block usage
  const isCanceled = billingStatus === 'CANCELED';
  const isUnpaid = billingStatus === 'UNPAID';
  const shouldShow = (isCanceled || isUnpaid) && subscription.plan === 'FREE';

  // Don't block the subscription or settings pages — admins need them to resubscribe or manage the org
  if (!shouldShow || !organization || location.pathname === '/subscription' || location.pathname === '/settings') return null;

  return (
    <div className='absolute inset-0 z-40 flex items-center justify-center bg-white/80 dark:bg-stone-950/80 backdrop-blur-sm'>
      <div className='max-w-md w-full mx-4 rounded-xl border border-slate-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl p-8 text-center'>
        {isCanceled ? (
          <div className='mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-slate-100 dark:bg-stone-800'>
            <XCircle className='size-6 text-slate-500 dark:text-stone-400' />
          </div>
        ) : (
          <div className='mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30'>
            <AlertTriangle className='size-6 text-red-500' />
          </div>
        )}

        <h2 className='text-xl font-semibold text-slate-900 dark:text-white mb-2'>
          {isCanceled ? t('expired.subscriptionExpired') : t('expired.subscriptionUnpaid')}
        </h2>

        <p className='text-sm text-slate-500 dark:text-stone-400 mb-6'>
          {isCanceled
            ? t('expired.canceledDescription')
            : t('expired.unpaidDescription')}
        </p>

        {hasRole('ADMIN') ? (
          <div className='flex flex-col gap-2'>
            <Button onClick={() => navigate('/subscription')} className='w-full gap-2'>
              <CreditCard className='size-4' />
              {isCanceled ? t('expired.subscribeNow') : t('expired.updatePayment')}
            </Button>
          </div>
        ) : (
          <p className='text-sm text-slate-500 dark:text-stone-400'>
            {t('expired.contactAdmin')}
          </p>
        )}
      </div>
    </div>
  );
};

export default SubscriptionExpiredOverlay;
